import { QueueService } from './../../queue/queue/queue.service';
import { HttpErrorResponse } from '@angular/common/http';
import { AfterViewInit, Component, ElementRef, OnDestroy, OnInit, ViewChild } from '@angular/core';
import { UntypedFormBuilder, UntypedFormGroup, Validators } from '@angular/forms';
import { PageName } from 'src/app/core/enums';
import { LoginRequest } from 'src/app/core/models/LoginRequest';
import { AuthService } from 'src/app/core/services/auth.service';
import { DefaultSettingService } from 'src/app/core/services/default-setting.service';
import { OtpService } from '../services/otp.service';
import { Location } from '@angular/common';
import { UserService } from 'src/app/core/user/user.service';
import { PreviousRouteService } from 'src/app/core/services/previous-route.service';
import { UtilsService } from 'src/app/core/services/utils.service';
import { SessionStorageService } from 'src/app/shared/storage/session-storage.service';
import { NavigationService } from 'src/app/core/services/navigation.service';
import { RoutingService } from 'src/app/home/services/routing.service';

@Component({
  selector: 'app-password-login',
  templateUrl: './password-login.component.html',
  styleUrls: ['./password-login.component.scss']
})
export class PasswordLoginComponent implements OnInit, AfterViewInit, OnDestroy {
  @ViewChild("password", { static: false }) passwordEl: ElementRef;
  telInpSetting = DefaultSettingService.telInpSetting;

  data: any = null;
  phoneNo: string = "";
  phoneNoExist: boolean = true;
  showPw: boolean = false;
  pwErr: HttpErrorResponse = null;
  isQueue: boolean = false;
  navigateToPayment: boolean = false;
  showContinueAsGuest: boolean = true;
  isSubmittedLogin: boolean = false;
  countryCode: string = "MY";
  doNotRemoveContinueAsGuest: boolean = false;
  isFromVoucher: boolean = false;
  isConfirmOrder: boolean = false;

  fgroup: UntypedFormGroup = this.fb.group({
    phoneNo: ['', [Validators.required]],
    password: ['', [Validators.required, Validators.minLength(8)]]
  });

  constructor(
    private fb: UntypedFormBuilder,
    private authService: AuthService,
    private otpService: OtpService,
    private queueService: QueueService,
    private location: Location,
    private userService: UserService,
    private previousRouteService: PreviousRouteService,
    private utilsService: UtilsService,
    private sessionStorageService: SessionStorageService,
    private navigationService: NavigationService,
    private routingService: RoutingService,
  ) {
    this.getFormControl('password').valueChanges.subscribe((dt) => {
      this.pwErr = null;
    })
  }

  get fg() { return this.fgroup }

  get f() { return this.fg.controls; }

  private getFormControl(formControlName: string) {
    return this.fgroup.controls[formControlName];
  }

  async ngOnInit() {
    let queueData = this.queueService.getQueueData();
    this.isQueue = queueData ? true : false;
    this.data = history.state?.data ?? null;

    // will be set in both cart and order summary to determine whether to navigate to payment page after login
    let cachePaymentFlag = JSON.parse(this.sessionStorageService.getItem("isPayment"));
    this.navigateToPayment = cachePaymentFlag ?? false;

    let isConfirmOrder = JSON.parse(this.sessionStorageService.getItem("isConfirmOrder"));
    this.isConfirmOrder = isConfirmOrder ?? false;

    let fromVoucher = JSON.parse(this.sessionStorageService.getItem("fromVoucher"));
    this.isFromVoucher = fromVoucher ? fromVoucher : false;

    this.showContinueAsGuest = this.userService.getShowContinueAsGuestFlag();
    this.isSubmittedLogin = this.authService.getSubmittedLogin();

    // if data pass in is remove and is queue then navigate to pre queue
    // else if no data, not queue and guest payment then back
    // else just move to login page
    if (!this.data && this.isQueue) {
      await this.queueService.navigateToPreQueue();
    }
    else if (!this.data && !this.isQueue && this.navigateToPayment) {
      this.location.back();
    }
    else if (!this.data && !this.isQueue && !this.navigateToPayment) {
      this.navigationService.navigate(["login"]);
    }

    this.phoneNoExist = history?.state?.phoneNoExist ?? true;
    this.phoneNo = history?.state?.data?.['phoneNo'] ?? "";
    this.fg.controls['phoneNo'].setValue(this.phoneNo);
    this.fg.controls['phoneNo'].disable();

    let dialCode = history?.state?.data?.['dialCode'] ?? "";
    let parsedPhoneData = await this.utilsService.parseGivenPhoneNumber("+" + dialCode + this.phoneNo);
    this.countryCode = parsedPhoneData.country;
  }

  ngAfterViewInit(): void {
    this.passwordEl.nativeElement.focus();
  }

  ngOnDestroy(): void {
    this.authService.removeSubmittedLoginFlag();
    if (!this.doNotRemoveContinueAsGuest)
      this.userService.removeShowContinueAsGuestFlag();
  }

  async onOtpLogin() {
    this.doNotRemoveContinueAsGuest = true;
    await this.otpService.onSendOTP(this.data.mobileObj);
    this.navigationService.navigate(["otp"], { state: { loginForm: this.data, phoneNoExist: this.phoneNoExist }, replaceUrl: true });
  }

  async onForgotPw() {
    await this.otpService.onSendOTP(this.data.mobileObj, PageName.ForgotPasswordPage);
    this.navigationService.navigate(["otp"], { state: { loginForm: this.data, phoneNoExist: this.phoneNoExist }, replaceUrl: true });
  }

  async onSubmit() {
    if (!this.fgroup.valid) {
      return;
    }

    let loginReq: LoginRequest = {
      mobileNo: this.data.dialCode + this.data.phoneNo,
      password: this.fg.controls['password'].value
    };

    let routeName: any;

    if (this.isFromVoucher || this.isConfirmOrder) {
      routeName = this.previousRouteService.getPaymentBackUrl();
    } else {
      if (this.navigateToPayment) {
        routeName = 'order-payment';
      }
    }

    this.queueService.removeCanQueueRegisterFlag();

    let successLogin = await this.authService.login(loginReq, routeName, this.isQueue);

    if (successLogin instanceof HttpErrorResponse) {
      this.fg.controls['password'].reset();
      this.pwErr = successLogin;
    }
  }

  onClickContinueAsGuest() {
    if (this.isQueue) {
      this.queueService.setCanQueueRegister(false);
      this.navigationService.navigate(['queue', 'complete-queue-profile'], { replaceUrl: true });
    } else if (this.isFromVoucher) {
      this.onClickBack();
    }
    else if (this.navigateToPayment) {
      let routeName = 'order-payment';
      if (routeName) {
        this.navigationService.navigate([routeName], { replaceUrl: true, state: { reinit: true } });
      } else {
        this.backPrevious();
      }
    }
    else {
      this.backPrevious();
    }

    this.sessionStorageService.removePaymentAndConfirmOrder();
  }

  backPrevious() {
    // get previous route from store area and remove after that
    let previousRoute = this.previousRouteService.getPreviousRoute();

    if (previousRoute) {
      this.navigationService.navigateByUrl(previousRoute.routeName, { replaceUrl: true });
      this.previousRouteService.removePreviousRoute();
    } else {
      this.routingService.navigateHomeUrl();
    }
  }

  onClickBack() {
    let backRoute = this.previousRouteService.getPaymentBackUrl();
    let previousURL = this.previousRouteService.getPreviousRoute();
    if (backRoute && !previousURL) {
      this.navigationService.navigateByUrl(backRoute);
    } else if (backRoute && previousURL) {
      this.navigationService.navigateByUrl(previousURL.routeName, { replaceUrl: true });
      this.previousRouteService.removePreviousRoute();
    } else {
      this.location.back();
    }

    this.sessionStorageService.removePaymentAndConfirmOrder();
  }
}
